/**
 * Processes email HTML content to extract body content and styles
 * Handles full HTML documents embedded in email content
 * @param {string} htmlContent - Raw HTML content from email API
 * @returns {string} - Processed HTML content ready for ProseMirror/KendoEditor
 * @author Generated for email rendering fix
 */
export const processEmailContent = (htmlContent) => {
  if (!htmlContent || typeof htmlContent !== "string") {
    return htmlContent || "";
  }

  try {
    // First, check if content contains full HTML document structure
    const hasDoctype = /<!DOCTYPE/i.test(htmlContent);
    const hasHtmlTag = /<html/i.test(htmlContent);
    const hasBodyTag = /<body/i.test(htmlContent);
    
    // If it doesn't look like a full document, return as-is
    if (!hasDoctype && !hasHtmlTag && !hasBodyTag) {
      return htmlContent;
    }

    // Create a temporary DOM parser to extract content
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, "text/html");

    // Check for parsing errors (DOMParser sometimes creates error elements)
    const parserError = doc.querySelector("parsererror");
    if (parserError) {
      // If parsing fails, try regex fallback
      return extractBodyContentRegex(htmlContent);
    }

    // Extract body content
    const bodyElement = doc.querySelector("body");
    let bodyContent = "";

    if (bodyElement) {
      bodyContent = bodyElement.innerHTML;
    } else {
      // If no body tag, try to find content after the head
      // This handles cases where HTML structure is malformed
      const htmlElement = doc.querySelector("html");
      if (htmlElement) {
        // Get all content that's not in head
        const allElements = Array.from(htmlElement.children);
        const bodyElements = allElements.filter((el) => el.tagName !== "HEAD");
        bodyContent = bodyElements.map((el) => el.outerHTML).join("");
      } else {
        // Fallback: try regex extraction
        return extractBodyContentRegex(htmlContent);
      }
    }

    // Clean up any remaining DOCTYPE or html/body tag remnants that might have been parsed incorrectly
    bodyContent = bodyContent.replace(/<!DOCTYPE[^>]*>/gi, "");
    bodyContent = bodyContent.replace(/<\/?html[^>]*>/gi, "");
    bodyContent = bodyContent.replace(/<\/?body[^>]*>/gi, "");

    return bodyContent || htmlContent;
  } catch (error) {
    console.error("Error processing email content:", error);
    // Fallback: try to extract body content using regex as last resort
    return extractBodyContentRegex(htmlContent);
  }
};

/**
 * Fallback method to extract body content using regex
 * @param {string} htmlContent - Raw HTML content
 * @returns {string} - Extracted body content
 */
const extractBodyContentRegex = (htmlContent) => {
  // Try to find body tag content
  const bodyMatch = htmlContent.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  if (bodyMatch && bodyMatch[1]) {
    return bodyMatch[1];
  }

  // Try to find content after </head> or </head>
  const headEndMatch = htmlContent.match(/<\/head>([\s\S]*)/i);
  if (headEndMatch && headEndMatch[1]) {
    // Remove closing html/body tags if present
    return headEndMatch[1].replace(/<\/html>[\s\S]*$/i, "").replace(/<\/body>[\s\S]*$/i, "");
  }

  // If no body found, return original content
  return htmlContent;
};

/**
 * Removes email metadata paragraphs (Email Task, Sender, Receiver, Subject)
 * Only removes metadata that appears BEFORE the HTML document starts
 * @param {string} htmlContent - HTML content that may contain metadata
 * @returns {string} - HTML content without metadata paragraphs
 */
export const removeEmailMetadata = (htmlContent) => {
  if (!htmlContent || typeof htmlContent !== "string") {
    return htmlContent || "";
  }

  try {
    // Find where the HTML document starts (DOCTYPE or <html> tag)
    const doctypeIndex = htmlContent.indexOf("<!DOCTYPE");
    const htmlIndex = htmlContent.indexOf("<html");
    const startIndex = doctypeIndex !== -1 ? doctypeIndex : (htmlIndex !== -1 ? htmlIndex : -1);
    
    // If no HTML document found, process the entire content
    if (startIndex === -1) {
      // No HTML document, just remove metadata from beginning
      return removeMetadataFromBeginning(htmlContent);
    }
    
    // Split content into metadata (before HTML) and email body (HTML document)
    const metadataSection = htmlContent.substring(0, startIndex);
    const htmlDocument = htmlContent.substring(startIndex);
    
    // Remove metadata only from the beginning section
    const cleanedMetadata = removeMetadataFromBeginning(metadataSection);
    
    // Return cleaned metadata + HTML document
    return cleanedMetadata + htmlDocument;
  } catch (error) {
    console.error("Error removing email metadata:", error);
    return htmlContent;
  }
};

/**
 * Removes metadata paragraphs from the beginning of content
 * Only removes consecutive metadata paragraphs at the start
 * @param {string} content - Content that may start with metadata
 * @returns {string} - Content without initial metadata paragraphs
 */
const removeMetadataFromBeginning = (content) => {
  if (!content || typeof content !== "string") {
    return content || "";
  }

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, "text/html");
    
    const metadataPatterns = [
      /^Email Task:/i,
      /^Sender:/i,
      /^Receiver:/i,
      /^Subject:/i,
      /^Description:/i,
    ];

    // Only remove metadata paragraphs that appear at the beginning
    const allParagraphs = doc.querySelectorAll("p");
    let removedCount = 0;
    
    for (let i = 0; i < allParagraphs.length; i++) {
      const p = allParagraphs[i];
      const text = p.textContent.trim();
      
      // Check if this paragraph matches metadata pattern
      if (metadataPatterns.some((pattern) => pattern.test(text))) {
        // Only remove if it's at the beginning (consecutive metadata)
        p.remove();
        removedCount++;
      } else {
        // Stop removing once we hit non-metadata content
        break;
      }
    }

    return doc.body ? doc.body.innerHTML : doc.documentElement.innerHTML;
  } catch (error) {
    console.error("Error removing metadata from beginning:", error);
    return content;
  }
};

/**
 * Extracts styles from <head> section of HTML document
 * @param {string} htmlContent - Raw HTML content from email API
 * @returns {string} - Combined CSS styles from all <style> tags
 */
export const extractEmailStyles = (htmlContent) => {
  if (!htmlContent || typeof htmlContent !== "string") {
    return "";
  }

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, "text/html");
    
    const styleTags = doc.querySelectorAll("head style");
    const styles = Array.from(styleTags)
      .map((styleTag) => styleTag.textContent)
      .filter((style) => style && style.trim())
      .join("\n");

    return styles;
  } catch (error) {
    console.error("Error extracting email styles:", error);
    // Fallback: try regex extraction
    const styleMatches = htmlContent.match(/<style[^>]*>([\s\S]*?)<\/style>/gi);
    if (styleMatches) {
      return styleMatches
        .map((match) => {
          const contentMatch = match.match(/<style[^>]*>([\s\S]*?)<\/style>/i);
          return contentMatch ? contentMatch[1] : "";
        })
        .filter((style) => style.trim())
        .join("\n");
    }
    return "";
  }
};

/**
 * Processes email content for direct HTML rendering (not through ProseMirror)
 * Preserves all styles and email-specific HTML structure
 * Does NOT remove any content from the email body - only extracts body from HTML document
 * @param {string} htmlContent - Raw HTML content from email API
 * @param {boolean} removeMetadata - Whether to remove initial metadata (default: false to preserve all content)
 * @returns {Object} - Object with bodyContent and styles
 */
export const processEmailForDirectRender = (htmlContent, removeMetadata = false) => {
  if (!htmlContent || typeof htmlContent !== "string") {
    return { bodyContent: htmlContent || "", styles: "" };
  }

  try {
    // Extract styles first (before any processing)
    const styles = extractEmailStyles(htmlContent);
    
    // Find where the HTML document starts
    const doctypeIndex = htmlContent.indexOf("<!DOCTYPE");
    const htmlIndex = htmlContent.indexOf("<html");
    const startIndex = doctypeIndex !== -1 ? doctypeIndex : (htmlIndex !== -1 ? htmlIndex : -1);
    
    let bodyContent = "";
    
    if (startIndex !== -1) {
      // HTML document found - extract body content from it
      const htmlDocument = htmlContent.substring(startIndex);
      bodyContent = processEmailContent(htmlDocument);
      
      // If metadata removal is requested, remove only the initial metadata paragraphs
      // that appear BEFORE the HTML document
      if (removeMetadata && startIndex > 0) {
        const metadataSection = htmlContent.substring(0, startIndex);
        const cleanedMetadata = removeMetadataFromBeginning(metadataSection);
        // Only prepend cleaned metadata if it's not empty and doesn't contain the HTML document
        if (cleanedMetadata.trim() && !cleanedMetadata.includes("<!DOCTYPE") && !cleanedMetadata.includes("<html")) {
          bodyContent = cleanedMetadata + bodyContent;
        }
      } else if (startIndex > 0 && !removeMetadata) {
        // If not removing metadata, include everything before HTML document
        const beforeHtml = htmlContent.substring(0, startIndex);
        bodyContent = beforeHtml + bodyContent;
      }
    } else {
      // No HTML document found - return content as-is
      if (removeMetadata) {
        bodyContent = removeEmailMetadata(htmlContent);
      } else {
        bodyContent = htmlContent;
      }
    }
    
    return { bodyContent, styles };
  } catch (error) {
    console.error("Error processing email for direct render:", error);
    return { bodyContent: htmlContent, styles: "" };
  }
};

/**
 * Main function to process email content for editor display
 * Combines body extraction and metadata removal
 * @param {string} htmlContent - Raw HTML content from email API
 * @param {boolean} removeMetadata - Whether to remove email metadata (default: false)
 * @returns {string} - Processed HTML content ready for editor
 */
export const processEmailForEditor = (htmlContent, removeMetadata = false) => {
  if (!htmlContent) {
    return "";
  }

  let processed = processEmailContent(htmlContent);
  
  if (removeMetadata) {
    processed = removeEmailMetadata(processed);
  }

  return processed;
};

